import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Users, Search, Edit, Building2, User } from "lucide-react";
import { ModuleHeaderIcon } from "@/components/ModuleHeader";

const HelpdeskContactos = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contactos, setContactos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterTipo, setFilterTipo] = useState("all");
  const [editDialog, setEditDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ empresa: "", email: "" });
  const canEditEmail = user?.role === "admin" || user?.role === "supervisor";

  useEffect(() => {
    fetchContactos();
  }, []);

  const fetchContactos = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/helpdesk/contactos`, { withCredentials: true });
      setContactos(res.data);
    } catch (err) {
      toast.error("Error al cargar contactos");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (c) => {
    setEditing(c);
    setForm({ empresa: c.empresa || "", email: c.email || "" });
    setEditDialog(true);
  };

  const handleSave = async () => {
    try {
      const payload = { empresa: form.empresa };
      if (canEditEmail) payload.email = form.email;
      await axios.put(`${API}/helpdesk/contactos/${encodeURIComponent(editing.id)}`, payload, { withCredentials: true });
      toast.success("Contacto actualizado");
      setEditDialog(false);
      fetchContactos();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al actualizar");
    }
  };

  const filtered = contactos.filter(c => {
    if (filterTipo !== "all" && c.tipo !== filterTipo) return false;
    if (search) {
      const s = search.toLowerCase();
      return c.nombre?.toLowerCase().includes(s) || c.email?.toLowerCase().includes(s) || c.empresa?.toLowerCase().includes(s);
    }
    return true;
  });

  const totalExternos = contactos.filter(c => c.tipo === "externo").length;
  const totalInternos = contactos.filter(c => c.tipo === "interno").length;

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="helpdesk-contactos-page">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <ModuleHeaderIcon icon={Users} />
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Helpdesk — Contactos</h1>
            <p className="text-slate-500">Directorio de personas que han creado tickets</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-slate-700">{contactos.length}</p>
              <p className="text-xs text-slate-500">Total</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{totalInternos}</p>
              <p className="text-xs text-slate-500">Internos</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">{totalExternos}</p>
              <p className="text-xs text-slate-500">Externos</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Buscar por nombre, email o empresa..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="contacto-search" />
          </div>
          <Select value={filterTipo} onValueChange={setFilterTipo}>
            <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="interno">Internos</SelectItem>
              <SelectItem value="externo">Externos</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>NOMBRE</TableHead>
                  <TableHead>EMAIL</TableHead>
                  <TableHead>EMPRESA</TableHead>
                  <TableHead>TIPO</TableHead>
                  <TableHead>TICKETS</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-10">Cargando...</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400">No hay contactos</TableCell></TableRow>
                ) : filtered.map(c => (
                  <TableRow key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/helpdesk/contactos/${encodeURIComponent(c.id)}`)} data-testid={`contacto-row-${c.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {c.picture ? (
                          <img src={c.picture} alt="" className="w-7 h-7 rounded-full" />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center">
                            <User className="w-3.5 h-3.5 text-slate-500" />
                          </div>
                        )}
                        <span className="font-medium">{c.nombre}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{c.email || "—"}</TableCell>
                    <TableCell className="text-sm">{c.empresa || <span className="text-slate-400 italic">Sin asignar</span>}</TableCell>
                    <TableCell>
                      <Badge className={c.tipo === "interno" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}>
                        {c.tipo === "interno" ? "Interno" : "Externo"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{c.ticket_count}</Badge>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleEdit(c); }} data-testid={`edit-contacto-${c.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={editDialog} onOpenChange={setEditDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Editar Contacto</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-slate-500">Nombre</Label>
                <p className="font-medium">{editing?.nombre}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Email</Label>
                {canEditEmail ? (
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="correo@ejemplo.com"
                    data-testid="contacto-email-input"
                  />
                ) : (
                  <p className="text-sm">{editing?.email || "—"}</p>
                )}
              </div>
              <div>
                <Label className="text-xs text-slate-500">Tipo</Label>
                <Badge className={editing?.tipo === "interno" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}>
                  {editing?.tipo === "interno" ? "Interno" : "Externo"}
                </Badge>
              </div>
              <div className="space-y-2">
                <Label>Empresa</Label>
                <Input value={form.empresa} onChange={(e) => setForm({ ...form, empresa: e.target.value })} placeholder="Nombre de la empresa" data-testid="contacto-empresa" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialog(false)}>Cancelar</Button>
              <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" data-testid="save-contacto-btn">Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default HelpdeskContactos;
